import { Injectable } from '@angular/core';

import { Observable, shareReplay } from 'rxjs';

import { Chip, Route, ParentRoute, SubPage, ParentSubPage, Facet, School, Partner, Question, Person, StoreName, Taxonomies } from '@app/constants';
import { createStore, propsArrayFactory, Store } from '@ngneat/elf';
import { withEntities, selectAllEntities, setEntities, upsertEntitiesById, selectMany, getEntity, updateAllEntities, updateEntities, selectEntity } from '@ngneat/elf-entities';

type TaxonomyStore = {
  _store: Store,
  data$: Observable<Taxonomies[]>
};


@Injectable({
  providedIn: 'root'
})
export class TaxonomyStoreService {

  private _taxonomyStores = new Map<StoreName, any>();
  private _taxonomyStore;
  private _facetsStore;
  

  public taxonomy$;
  public facets$;

  constructor() {

    /* TAXONOMY STORE */

    this._taxonomyStore = createStore(
      { name: 'taxonomy' },
      withEntities<any, 'url'>({idKey: 'url'})
    );

    this.taxonomy$ = this._taxonomyStore.pipe(selectAllEntities(), shareReplay({ bufferSize: 1, refCount: true }));


    /* FACETS STORE */

    const {
      withCollectionIds,
      selectCollectionIds,
      addCollectionIds,
      removeCollectionIds,
      inCollectionIds,
    } = propsArrayFactory('collectionIds', { initialValue: [] as string[] });

    this._facetsStore = createStore(
      { name: 'facets' },
      withEntities<Facet, 'entityId'>({idKey: 'entityId'})
    );

    this.facets$ = this._facetsStore.pipe(selectAllEntities(), shareReplay({ bufferSize: 1, refCount: true }));

  }

  public setTaxonomyStore = (taxonomies: Taxonomies[]) => {

    this._taxonomyStore.update(
      setEntities(taxonomies)
    );
  };

  public setFacetsStore = (taxonomies: Facet[]) => {

    this._facetsStore.update(
      setEntities(taxonomies)
    );
  };

  /* PARENT STORE */

  public setParentStore = (storeName: StoreName, taxonomies: Taxonomies[]) => {
    //console.log('StoreService setParentStore storeName ', storeName);

    const _store = this._getStore(storeName)._store;
    _store.update(
      setEntities(taxonomies),
      updateAllEntities({children: []})
    );

  };

  public addChild = (storeName: StoreName, entityId: string, child: Route) => {

    const _store = this._taxonomyStores.get(storeName)._store;
    //console.log('StoreService setParentStore _store ', _store);

    _store.update(updateEntities(entityId, (entity) => ({ ...entity, children: [...entity.children, child] })));
  };

  public setStore = (storeName: StoreName, taxonomies:  Taxonomies[]): void => {
    //console.log('StoreService setStore storeName ', storeName);
    
    const _storeApi = this._getStore(storeName);
    _storeApi._store.update(setEntities(taxonomies));
    
  };

  public getTaxonomyEntities$ = (ids: string[]): Observable<Taxonomies[]> => this._taxonomyStore.pipe(selectMany(ids), shareReplay({ bufferSize: 1, refCount: true }));
  public getTaxonomyEntity = (id: string): Taxonomies => this._taxonomyStore.query(getEntity(id));

  public getEntity = (storeName: StoreName, id: string): Taxonomies => {
    const store = this._taxonomyStores.get(storeName)._store;
    return store.query(getEntity(id));
  };
  
  public getEntities = (storeName: StoreName, ids: any): Taxonomies[] => {
    const store = this._taxonomyStores.get(storeName)._store;

    let entities = [];

    for (var i = 0; i < ids.length; ++i) {
      const entity = store.query(getEntity(ids[i]));

      if (entity !== undefined) {
        entities.push(entity);
      }
    }
    return entities;
  }

  
  /* DYNAMIC STORES */

  public getTaxonomyPages$ = (storeName: StoreName): Observable<Taxonomies[]> => this._getStore(storeName).data$;
  public getSectionPages$ = (storeName: StoreName, entityId: string): Taxonomies => this._getStore(storeName)._store.query(getEntity(entityId));

  private _getStore = (storeName: StoreName): TaxonomyStore => {
    
    if (this._taxonomyStores.has(storeName)) {
      return this._taxonomyStores.get(storeName);
    }
    else {
      const _storeApi = this._createStore(storeName);

      this._taxonomyStores.set(storeName, _storeApi);

      return _storeApi;
    }
  }

  private _createStore = (storeName: string): TaxonomyStore => {

    const _store = createStore(
      { name: storeName },
      withEntities<Taxonomies, 'entityId'>({idKey: 'entityId'})
    );

    const data$ = _store.pipe(selectAllEntities(), shareReplay({ bufferSize: 1, refCount: true })); 

    return {
      _store,
      data$
    };
  };
}
